import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  Link,
  ListSubheader,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  User as UserIcon,
  Users as UsersIcon,
  Bookmark as BookMarkIcon,
  Archive as PayerIcon,
  Server as LogIcon,
  Trash2 as BinIcon,
  Radio as StreamIcon
} from 'react-feather';
import NavItem from './NavItem';
import PerfectScrollbar from 'react-perfect-scrollbar';
import useAuth from 'src/hooks/useAuth';
import Logo from 'src/components/Logo';
import useAppInfo from 'src/hooks/useAppInfo';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import { HomeWork } from '@material-ui/icons';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
const sections = [
  {
    subheader: 'Reports',
    items: [
      {
        href: '/app/dashboard',
        icon: BarChartIcon,
        title: 'Dashboard'
      }
    ]
  },
  {
    subheader: 'Management',
    items: [
      {
        href: '/app/customers',
        icon: UsersIcon,
        title: 'Students'
      },
      {
        href: '/app/teachers',
        icon: UsersIcon,
        title: 'Teachers'
      },
      {
        href: '/app/payers',
        icon: PayerIcon,
        title: 'Payers'
      },
      {
        href: '/app/courses',
        icon: OndemandVideoIcon,
        title: 'Courses'
      },
      {
        href: '/app/live-streams',
        icon: StreamIcon,
        title: 'Streams'
      },
      {
        href: '/app/subjects',
        icon: BookMarkIcon,
        title: 'Subjects'
      },
      {
        href: '/app/management/invoices',
        icon: AttachMoneyIcon,
        title: 'Invoices'
      },
      {
        href: '/app/management/logs',
        icon: LogIcon,
        title: 'Logs'
      },
      {
        href: '/app/management/bin',
        icon: BinIcon,
        title: 'Bin'
      },
      {
        href: '/app/account',
        icon: UserIcon,
        title: 'Account'
      },
      {
        href: '/app/settings',
        icon: SettingsIcon,
        title: 'Settings'
      }
    ]
  }
];

const admin_sections = [
  {
    subheader: 'Reports',
    items: [
      {
        href: '/app/dashboard',
        icon: BarChartIcon,
        title: 'Dashboard'
      }
    ]
  },
  {
    subheader: 'Management',
    items: [
      {
        href: '/app/customers',
        icon: UsersIcon,
        title: 'Students'
      },
      {
        href: '/app/teachers',
        icon: UsersIcon,
        title: 'Teachers'
      },
      {
        href: '/app/payers',
        icon: PayerIcon,
        title: 'Payers'
      },
      {
        href: '/app/courses',
        icon: OndemandVideoIcon,
        title: 'Courses'
      },
      {
        href: '/app/live-streams',
        icon: StreamIcon,
        title: 'Streams'
      },
      {
        href: '/app/subjects',
        icon: BookMarkIcon,
        title: 'Subjects'
      },
      {
        href: '/app/management/invoices',
        icon: AttachMoneyIcon,
        title: 'Invoices'
      },

      {
        href: '/app/account',
        icon: UserIcon,
        title: 'Account'
      },
      {
        href: '/app/settings',
        icon: SettingsIcon,
        title: 'Settings'
      }
    ]
  }
];

const ver_sections = [
  {
    subheader: 'Management',
    items: [
      {
        href: '/app/customers',
        icon: UsersIcon,
        title: 'Students'
      }
    ]
  }
];
const teacher_sections = [
  {
    subheader: 'Management',
    items: [
      {
        href: '/app/dashboard',
        icon: BarChartIcon,
        title: 'Dashboard'
      },
      {
        href: '/app/customers',
        icon: UsersIcon,
        title: 'Students'
      }
    ]
  }
];

const exam_sections = [
  {
    subheader: 'Exam Center',
    items: [
      {
        href: '/app/exam-center',
        icon: HomeWork,
        title: 'Exam Center'
      }
    ]
  }
];

const achivement_sections = [
  {
    subheader: 'Achivements',
    items: [
      {
        href: '/app/student-achivements',
        icon: HomeWork,
        title: 'Student Achivements'
      }
    ]
  }
];

function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();
  const { appInfo } = useAppInfo();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/app/account">
              <Avatar
                alt="User"
                className={classes.avatar}
                src={
                  user.avatar_url
                    ? "https://api.apluseducation.lk/" + user.avatar_url_new
                    : ''
                }
              />
            </RouterLink>
          </Box>
          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {user.fname + ' ' + user.lname}
            </Link>
            <Typography variant="body2" color="textSecondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {user.role === 'VER_ADMIN' || user.role === 'PAYER_ADMIN'
            ? ver_sections.map(section => (
                <List
                  key={section.subheader}
                  subheader={
                    <ListSubheader disableGutters disableSticky>
                      {section.subheader}
                    </ListSubheader>
                  }
                >
                  {renderNavItems({
                    items: section.items,
                    pathname: location.pathname
                  })}
                </List>
              ))
            : user.role === 'TEACHER'
            ? teacher_sections.map(section => (
                <List
                  key={section.subheader}
                  subheader={
                    <ListSubheader disableGutters disableSticky>
                      {section.subheader}
                    </ListSubheader>
                  }
                >
                  {renderNavItems({
                    items: section.items,
                    pathname: location.pathname
                  })}
                </List>
              ))
            : user.role === 'ADMIN'
            ? admin_sections.map(section => (
                <List
                  key={section.subheader}
                  subheader={
                    <ListSubheader disableGutters disableSticky>
                      {section.subheader}
                    </ListSubheader>
                  }
                >
                  {renderNavItems({
                    items: section.items,
                    pathname: location.pathname
                  })}
                </List>
              ))
              : user.role === 'EXAM_CENTER'
              ? exam_sections.map(section => (
                  <List
                    key={section.subheader}
                    subheader={
                      <ListSubheader disableGutters disableSticky>
                        {section.subheader}
                      </ListSubheader>
                    }
                  >
                    {renderNavItems({
                      items: section.items,
                      pathname: location.pathname
                    })}
                  </List>
                ))
            : sections.map(section => (
                <List
                  key={section.subheader}
                  subheader={
                    <ListSubheader disableGutters disableSticky>
                      {section.subheader}
                    </ListSubheader>
                  }
                >
                  {renderNavItems({
                    items: section.items,
                    pathname: location.pathname
                  })}
                </List>
              ))}
              {achivement_sections.map(section => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
        <Divider />
        {/* <Box p={2}>
          <Box p={2} borderRadius="borderRadius" bgcolor="background.dark">
            <Typography variant="h6" color="textPrimary">
              Developed by Axoten Innovation
            </Typography>
          </Box>
        </Box> */}
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
